import { Controller } from '@hotwired/stimulus';
import Sortable from 'sortablejs';

/**
 * RegularizationTemplates::TableComponent companion controller.
 *
 * Owns four concerns for the nested-attrs templates table:
 *   1. add  — clone the hidden <template> row (with __NEW_INDEX__ → Date.now())
 *             and append to the rows container
 *   2. remove — flag persisted rows with _destroy=1 (hide) or rip out unsaved rows
 *   3. filter — show/hide rows by category in response to filter-pills:change
 *   4. reorder — owns its OWN SortableJS instance (NOT the project's
 *      sortable_controller.js, which POSTs to a urlValue endpoint and does not
 *      mutate hidden [position] fields). On drag end, rewrites every visible
 *      row's [position] hidden field to its new index so the parent form
 *      submits with correct positions.
 *
 * Action map:
 *   click->regularization-template-builder#add        (Ajouter une ligne button)
 *   click->regularization-template-builder#remove     (per-row trash button)
 *   change->regularization-template-builder#updateRowCategory  (category select)
 *   filter-pills:change@window->regularization-template-builder#filter
 */
export default class extends Controller {
  static targets = ['rowsContainer', 'row', 'template', 'table', 'emptyState'];

  connect() {
    this.activeFilter = 'all';
    this.toggleTable();
    this._initSortable();
  }

  disconnect() {
    this.sortable?.destroy();
  }

  _initSortable() {
    this.sortable = Sortable.create(this.rowsContainerTarget, {
      handle: '.sortable-handle',
      animation: 150,
      filter: '.hidden',
      onEnd: () => this._rewritePositionFields(),
    });
  }

  _rewritePositionFields() {
    this.rowTargets.forEach((row, idx) => {
      const positionInput = row.querySelector(
        '[data-regularization-template-builder-target="positionField"]'
      );
      if (positionInput) positionInput.value = idx;
    });
  }

  add(event) {
    event.preventDefault();
    const newIndex = Date.now();
    const html = this.templateTarget.innerHTML.replace(/__NEW_INDEX__/g, newIndex);
    this.rowsContainerTarget.insertAdjacentHTML('beforeend', html);
    this.toggleTable();
    this.applyFilter(this.activeFilter);
    this._rewritePositionFields();
  }

  remove(event) {
    event.preventDefault();
    const row = event.currentTarget.closest('tr');
    const destroyField = row.querySelector(
      '[data-regularization-template-builder-target="destroyField"]'
    );
    const idField = row.querySelector('input[name$="[id]"]');

    if (idField && idField.value) {
      destroyField.value = '1';
      row.classList.add('hidden');
    } else {
      row.remove();
    }
    this.toggleTable();
    this._rewritePositionFields();
  }

  filter(event) {
    this.activeFilter = event.detail.value;
    this.applyFilter(this.activeFilter);
  }

  applyFilter(value) {
    this.rowTargets.forEach((row) => {
      const category = row.dataset.category;
      const matches = value === 'all' || category === value;
      const destroyField = row.querySelector(
        '[data-regularization-template-builder-target="destroyField"]'
      );
      const destroyed = destroyField && destroyField.value === '1';
      if (matches && !destroyed) {
        row.classList.remove('hidden');
      } else {
        row.classList.add('hidden');
      }
    });
  }

  updateRowCategory(event) {
    const row = event.currentTarget.closest('tr');
    row.dataset.category = event.currentTarget.value;
    this.applyFilter(this.activeFilter);
  }

  toggleTable() {
    if (!this.hasTableTarget) return;
    const visibleRows = this.rowTargets.filter((r) => {
      const destroyField = r.querySelector(
        '[data-regularization-template-builder-target="destroyField"]'
      );
      return !(destroyField && destroyField.value === '1');
    });
    const isEmpty = visibleRows.length === 0;
    this.tableTarget.classList.toggle('hidden', isEmpty);
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle('hidden', !isEmpty);
    }
  }
}
